import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Platform } from '../ridango.models';

@Injectable({ providedIn: 'root' })
export class KeyboardService implements OnDestroy {
    private readonly keyboardVisibleClass = 'keyboard-visible';
    private readonly keyboardListeners: PluginListenerHandle[] = [];

    constructor(private readonly ngZone: NgZone) {
        if (Capacitor.isNativePlatform()) {
            // Keep keyboard overlaying content on iOS to avoid layout jump.
            if (Capacitor.getPlatform() === Platform.IOS) {
                Keyboard.setResizeMode({ mode: KeyboardResize.None });
            }

            this.initializeKeyboardVisibilityListeners();

            // Use passive: true for performance
            document.addEventListener(
                'scroll',
                () => {
                    Keyboard.hide();
                },
                { passive: true }
            );
            document.addEventListener('touchstart', this.handleTouch.bind(this), { passive: true });
        }
    }

    ngOnDestroy(): void {
        this.keyboardListeners.forEach((listener) => listener.remove());
        this.keyboardListeners.length = 0;
    }

    // Manual method to hide the keyboard
    // Useful to be called by scrolling ion elements which don't trigger the document scroll event
    onScroll() {
        if (Capacitor.isNativePlatform()) {
            Keyboard.hide();
        }
    }

    // Hide the keyboard when the user touches outside an input-like field
    handleTouch(event: TouchEvent) {
        const target = event.target as HTMLElement;
        if (
            target?.closest('input, textarea, [contenteditable="true"]') ||
            target?.closest('ion-searchbar') ||
            target?.closest('mat-form-field') ||
            target?.closest('ion-input')
        ) {
            return;
        }

        // Due to iOS we need to let event propagate (microtask queue) with touchstart
        // If we don't the keyboard will reopen and touch event does not propagate
        // Alternative is to use 'touchend' event, but that does not feel right form UX
        setTimeout(() => {
            Keyboard.hide();
        }, 100);
    }

    private initializeKeyboardVisibilityListeners(): void {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            const onKeyboardShow = (info?: { keyboardHeight?: number }) => {
                if (info?.keyboardHeight) {
                    document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
                }

                this.toggleKeyboardVisible(true);
            };

            const onKeyboardHide = () => {
                document.body.style.removeProperty('--keyboard-height');
                this.toggleKeyboardVisible(false);
            };

            Keyboard.addListener('keyboardWillShow', onKeyboardShow).then((listener) =>
                this.keyboardListeners.push(listener)
            );

            Keyboard.addListener('keyboardDidShow', onKeyboardShow).then((listener) =>
                this.keyboardListeners.push(listener)
            );

            Keyboard.addListener('keyboardWillHide', onKeyboardHide).then((listener) =>
                this.keyboardListeners.push(listener)
            );

            Keyboard.addListener('keyboardDidHide', onKeyboardHide).then((listener) =>
                this.keyboardListeners.push(listener)
            );
        });
    }

    /**
     * Toggles a CSS class on the document body indicating whether the native keyboard is visible.
     */
    private toggleKeyboardVisible(visible: boolean): void {
        const targets: Array<Element | null> = [
            document.body,
            document.documentElement,
            document.querySelector('ion-app')
        ];

        targets.forEach((el) => {
            if (!el) {
                return;
            }

            if (visible) {
                el.classList.add(this.keyboardVisibleClass);
            } else {
                el.classList.remove(this.keyboardVisibleClass);
            }
        });
    }
}
